import { OPS_COLORS, getOperationalScoreHeatColor } from '../constants/operationsDashboardColors';

export function getRowHighlight(row: any): string | undefined {
  if (row.coverageStatus === 'NOT_COVERED' && row.requiresCoverage) return '#fff3e0';
  if (row.isCoveringOthers) return '#e3f2fd';
  if (row.attendanceStatus === 'SLIGHTLY_LATE') return '#e3f2fd';
  if (row.attendanceStatus === 'LATE') return '#fffde7';
  if (row.attendanceStatus === 'PRESENT') return '#e8f5e9';
  if (['ABSENT', 'NO_CALL_NO_SHOW'].includes(row.attendanceStatus)) return '#ffebee';
  if (row.attendanceStatus === 'SCHEDULED_OFF') return '#f5f5f5';
  return undefined;
}

/** Present on shift with no outstanding uncovered coverage need (green row). */
export function isHealthGreenRow(row: any): boolean {
  if (!row) return false;
  if (row.attendanceStatus === 'EXPECTED') return true;
  if (row.attendanceStatus !== 'PRESENT') return false;
  if (row.coverageStatus === 'NOT_COVERED' && row.requiresCoverage) return false;
  return true;
}

export function canShowAssignCoverageMenuItem(row: any): boolean {
  if (!row) return false;
  if (row.coverageAssignmentId) return true;
  if (!row.requiresCoverage) return false;
  return !isHealthGreenRow(row);
}

/** Allow adding another non-overlapping partial when one partial already exists. */
export function canShowAddCoverageMenuItem(row: any): boolean {
  if (!row) return false;
  if (row.coverageStatus === 'PARTIALLY_COVERED') return true;
  return Boolean(row.coverageAssignmentCount && row.coverageAssignmentCount > 0
    && row.coverageStatus !== 'COVERED'
    && row.coverageStatus !== 'NOT_REQUIRED');
}

export function canShowMarkCoverageNotRequired(row: any): boolean {
  if (!row) return false;
  if (isHealthGreenRow(row)) return false;
  if (row.coverageStatus === 'NOT_REQUIRED') return false;
  return true;
}

export function getAttendanceDetailColor(status: string): string {
  if (status === 'PRESENT') return OPS_COLORS.present;
  if (status === 'SLIGHTLY_LATE') return '#64b5f6';
  if (status === 'LATE') return OPS_COLORS.late;
  if (['ABSENT', 'NO_CALL_NO_SHOW'].includes(status)) return OPS_COLORS.critical;
  if (status === 'SCHEDULED_OFF') return OPS_COLORS.scheduledOff;
  return OPS_COLORS.expected;
}

export function getOperationalScoreColor(level?: string, score?: number): string {
  if (typeof score === 'number') return getOperationalScoreHeatColor(score);
  if (level === 'excellent') return OPS_COLORS.excellent;
  if (level === 'good') return OPS_COLORS.good;
  if (level === 'warning') return OPS_COLORS.warning;
  return OPS_COLORS.criticalScore;
}

export const AUTO_REFRESH_OPTIONS = [
  { label: 'Off', value: 0 },
  { label: '30 seconds', value: 30000 },
  { label: '60 seconds', value: 60000 },
  { label: '5 minutes', value: 300000 },
];

export { ABSENCE_REASON_OPTIONS, formatAbsenceReasonLabel } from '../constants/absenceReasons';

export const COVERAGE_REASON_OPTIONS = [
  'QUEUE_COVERAGE', 'FULL_SHIFT_COVERAGE', 'PARTIAL_SHIFT_COVERAGE', 'BREAK_COVERAGE',
  'EMERGENCY_COVERAGE', 'CROSS_CAMPAIGN_SUPPORT', 'OTHER',
];

export type CoverageTimeOption = 'ORIGINAL_SCHEDULE' | 'CUSTOM';
export type CoverageModeOption = 'FULL_TRANSFER' | 'SHARED_COVERAGE';

export function timeOptionFromCoverageStatus(status: string): CoverageTimeOption {
  return status === 'COVERED' ? 'ORIGINAL_SCHEDULE' : 'CUSTOM';
}

export function coverageStatusRequiresCoveringAgent(status: string): boolean {
  return status !== 'NOT_COVERED' && status !== 'NOT_REQUIRED';
}

export function usesOriginalScheduleForCoverageStatus(status: string): boolean {
  return status === 'COVERED';
}

export function getCoverageTimesForStatus(params: {
  coverageStatus: string;
  scheduledStartTime: string;
  scheduledEndTime: string;
  existingStartTime?: string;
  existingEndTime?: string;
  keepPartialTimes?: boolean;
}): { coverageStartTime: string; coverageEndTime: string } {
  const scheduledStart = normalizeTimeForInput(params.scheduledStartTime);
  const scheduledEnd = normalizeTimeForInput(params.scheduledEndTime || params.scheduledStartTime);

  if (params.coverageStatus === 'COVERED') {
    return { coverageStartTime: scheduledStart, coverageEndTime: scheduledEnd };
  }

  if (params.coverageStatus === 'PARTIALLY_COVERED') {
    if (params.keepPartialTimes && params.existingStartTime) {
      return {
        coverageStartTime: normalizeTimeForInput(params.existingStartTime),
        coverageEndTime: normalizeTimeForInput(params.existingEndTime || scheduledEnd),
      };
    }
    return {
      coverageStartTime: getCurrentTimeHHmm(),
      coverageEndTime: normalizeTimeForInput(params.existingEndTime || scheduledEnd),
    };
  }

  return {
    coverageStartTime: normalizeTimeForInput(params.existingStartTime || scheduledStart),
    coverageEndTime: normalizeTimeForInput(params.existingEndTime || scheduledEnd),
  };
}

export function formatCoverageModeLabel(mode?: string | null): string {
  if (mode === 'FULL_TRANSFER') return 'Full Transfer';
  if (mode === 'SHARED_COVERAGE') return 'Shared Coverage';
  return '-';
}

export function normalizeTimeForInput(value?: string | null): string {
  if (!value) return '';
  const parts = value.split(':');
  if (parts.length >= 2) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
  }
  return value;
}

export function getCurrentTimeHHmm(): string {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

export function deriveCoverageTypeAndReason(
  coverageStatus: string,
  coverageMode?: string | null
): { coverageType: string; coverageReason: string } {
  if (coverageStatus === 'COVERED') {
    return { coverageType: 'FULL_SHIFT', coverageReason: 'FULL_SHIFT_COVERAGE' };
  }
  if (coverageStatus === 'PARTIALLY_COVERED') {
    if (coverageMode === 'SHARED_COVERAGE') {
      return { coverageType: 'QUEUE_ONLY', coverageReason: 'QUEUE_COVERAGE' };
    }
    return { coverageType: 'PARTIAL_SHIFT', coverageReason: 'PARTIAL_SHIFT_COVERAGE' };
  }
  if (coverageStatus === 'NOT_REQUIRED') {
    return { coverageType: 'FULL_SHIFT', coverageReason: 'FULL_SHIFT_COVERAGE' };
  }
  return { coverageType: 'TEMPORARY', coverageReason: 'EMERGENCY_COVERAGE' };
}

export function applyCoverageStatusToForm<T extends {
  coverageStatus: string;
  coverageMode?: string | null;
  scheduledStartTime: string;
  scheduledEndTime: string;
  coverageStartTime: string;
  coverageEndTime: string;
  coverageType: string;
  coverageReason: string;
}>(form: T, coverageStatus: string, options?: { keepPartialTimes?: boolean }): T {
  const times = getCoverageTimesForStatus({
    coverageStatus,
    scheduledStartTime: form.scheduledStartTime,
    scheduledEndTime: form.scheduledEndTime,
    existingStartTime: form.coverageStartTime,
    existingEndTime: form.coverageEndTime,
    keepPartialTimes: options?.keepPartialTimes ?? coverageStatus === 'PARTIALLY_COVERED',
  });
  const derived = deriveCoverageTypeAndReason(coverageStatus, form.coverageMode);
  return { ...form, coverageStatus, ...times, ...derived };
}
